import type { AxiosInstance, AxiosResponse } from "axios";
import type {
	InsightCurrentSnapshotResponse,
	InsightDiscoverySourceItem,
	InsightEnvelope,
	InsightFindingsResponse,
	InsightProcessSnapshotsResponse,
	InsightSourceItem,
	InsightSourceType,
	InsightTopologyResponse,
	InsightTopologyScopesResponse,
} from "../types/insightApi";

/**
 * Insight HTTP API client (CTX-97), aligned with `docs/insight-api-contract.md`
 * on `chopper-backend`'s `daniel/ctx-96-expose-tenant-authorized-insight-apis-through-chopper`.
 *
 * The shared `useAxiosAuth0()` client defaults to `${REACT_APP_API_URL}/v1`;
 * Insight lives under `/api/v2/insight/*` like Audit Logs and Reflector V2,
 * so every call here overrides `baseURL` to `.../v2` per request (keeps the
 * Auth0 interceptor active — see `auditLogs/requests/request.ts`).
 *
 * Every function accepts an optional `AbortSignal` so callers (via
 * `useInsightAsync`) can cancel in-flight requests on unmount / scope change
 * and never apply a stale response to newer UI state.
 *
 * Nothing here ever sends an S3 bucket, S3 key, or CTXR membership
 * assertion. Partner/global-admin customer views do send the currently
 * selected `tenantId`, matching BGP Visibility; `tenantMiddleware` then
 * authorizes that tenant against the authenticated user's hierarchy.
 * `sourceId`/`ctxrUuid` are always re-authorized server-side as well.
 *
 * Source discovery (Reflector/Cluster + Cloud/Account/Region) is NOT done
 * through `listTopologySources`/`listTopologyScopes` below — CTX-97's
 * "real-data milestone" correction requires reusing the existing,
 * already-authenticated BGP Visibility sidebar (`getCloudInventorySidebar`,
 * see `useCloudInventorySource`) instead of standing up a second discovery
 * pipeline. `listTopologySources`/`listTopologyScopes` are kept only as a
 * documented, tested contract shape in case a future health/freshness
 * enrichment pass wants them (per the ticket's explicit fallback clause) —
 * nothing in the runtime view calls them today.
 */

function getInsightV2BaseUrl(): string {
	const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3000";
	return `${apiBase}/v2`;
}

const INSIGHT_PATH = "/insight";

export interface ListTopologySourcesParams {
	type: InsightSourceType;
	cursor?: string | null;
	limit?: number;
}

export function listTopologySources(
	api: AxiosInstance,
	params: ListTopologySourcesParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightSourceItem[]>>> {
	return api.get<InsightEnvelope<InsightSourceItem[]>>(`${INSIGHT_PATH}/topology/sources`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			type: params.type,
			cursor: params.cursor || undefined,
			limit: params.limit,
		},
	});
}

export interface SourceScopeParams {
	sourceType: InsightSourceType;
	/** Canonical opaque id — see `canonicalSourceId()` in `../utils/insightScopeTree.ts`. */
	sourceId: string;
	/** Active customer tenant; server-side tenantMiddleware authorizes it. */
	tenantId?: number;
}

export function listTopologyScopes(
	api: AxiosInstance,
	params: SourceScopeParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightTopologyScopesResponse>>> {
	return api.get<InsightEnvelope<InsightTopologyScopesResponse>>(`${INSIGHT_PATH}/topology/scopes`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			sourceType: params.sourceType,
			sourceId: params.sourceId,
			tenantId: params.tenantId,
		},
	});
}

export interface TopologyOrFindingsParams extends SourceScopeParams {
	cloud: string;
	region: string;
	account?: string | null;
}

export function getTopology(
	api: AxiosInstance,
	params: TopologyOrFindingsParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightTopologyResponse>>> {
	return api.get<InsightEnvelope<InsightTopologyResponse>>(`${INSIGHT_PATH}/topology`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			sourceType: params.sourceType,
			sourceId: params.sourceId,
			cloud: params.cloud,
			region: params.region,
			account: params.account || undefined,
			tenantId: params.tenantId,
		},
	});
}

export function getFindings(
	api: AxiosInstance,
	params: TopologyOrFindingsParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightFindingsResponse>>> {
	return api.get<InsightEnvelope<InsightFindingsResponse>>(`${INSIGHT_PATH}/findings`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			sourceType: params.sourceType,
			sourceId: params.sourceId,
			cloud: params.cloud,
			region: params.region,
			account: params.account || undefined,
			tenantId: params.tenantId,
		},
	});
}

export interface ListDiscoverySourcesParams {
	cursor?: string | null;
	limit?: number;
}

export function listDiscoverySources(
	api: AxiosInstance,
	params?: ListDiscoverySourcesParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightDiscoverySourceItem[]>>> {
	return api.get<InsightEnvelope<InsightDiscoverySourceItem[]>>(`${INSIGHT_PATH}/discovery/sources`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			cursor: params?.cursor || undefined,
			limit: params?.limit,
		},
	});
}

export interface ProcessSnapshotsBody {
	sourceType: InsightSourceType;
	sourceId: string;
	ctxrUuid?: string;
}

export function processSnapshots(
	api: AxiosInstance,
	body: ProcessSnapshotsBody,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightProcessSnapshotsResponse>>> {
	return api.post<InsightEnvelope<InsightProcessSnapshotsResponse>>(
		`${INSIGHT_PATH}/snapshots/process`,
		body,
		{
			baseURL: getInsightV2BaseUrl(),
			signal,
		}
	);
}

export function getCurrentSnapshot(
	api: AxiosInstance,
	params: SourceScopeParams,
	signal?: AbortSignal
): Promise<AxiosResponse<InsightEnvelope<InsightCurrentSnapshotResponse>>> {
	return api.get<InsightEnvelope<InsightCurrentSnapshotResponse>>(`${INSIGHT_PATH}/snapshots/current`, {
		baseURL: getInsightV2BaseUrl(),
		signal,
		params: {
			sourceType: params.sourceType,
			sourceId: params.sourceId,
			tenantId: params.tenantId,
		},
	});
}
